// // Include the USWDS Accordion script.
// // This makes the component available globally. If you're only using it on certain pages,
// // include it on a template-specific script file instead.
// // Be sure to initialize any components as well (see init() function below.)

import accordion from '@uswds/uswds/js/usa-accordion';
import inPageNavigation from '@uswds/uswds/js/usa-in-page-navigation';
import navigation from '@uswds/uswds/js/usa-header';
import banner from '@uswds/uswds/js/usa-banner';
import tooltip from '@uswds/uswds/js/usa-tooltip';
import table from '@uswds/uswds/js/usa-table';
import Drupal from 'drupal';
import once from 'once';
import handbookOnPageNav from '../04-components/in-page-navigation/in-page-navigation.es6';

const MOBILE_NAV_ACTIVE_CLASS = 'usa-js-mobile-nav--active';
const NAV_SELECTOR = '.usa-nav';
const ACCORDION_SELECTOR = '.usa-accordion, .usa-accordion--bordered';
const SIDEBAR_ACCORDION_BUTTON_SELECTOR =
  '#sidenav-title, #sidebar-title, #filter-title';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]',
].join(',');

let mobileNavFocusTrapBound = false;

function getAccordionRoots(context) {
  const roots = [];
  if (context.matches && context.matches(ACCORDION_SELECTOR)) {
    roots.push(context);
  }

  if (context.querySelectorAll) {
    roots.push(...context.querySelectorAll(ACCORDION_SELECTOR));
  }

  return roots;
}

function gessoUswdsAccordions(context) {
  const roots = getAccordionRoots(context).filter(
    root => !root.querySelector(SIDEBAR_ACCORDION_BUTTON_SELECTOR)
  );

  once('hhsds-uswds-accordion', roots).forEach(root => {
    accordion.on(root);
  });
}

function gessoUswdsNavigation(context) {
  const subnav = context.querySelectorAll('.c-menu__subnav');
  subnav.forEach((menu, index) => {
    const button = menu.previousElementSibling;
    if (button.classList.contains('usa-nav__link')) {
      const id = menu.id || `extended-nav-section-${index}`;
      menu.id = id;
      button.setAttribute('aria-controls', id);
    }
  });
  navigation.on(context === document ? document.body : context);
}

function isVisible(element) {
  const style = window.getComputedStyle(element);
  return (
    !element.hidden &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    element.getClientRects().length > 0
  );
}

function getMobileNavFocusables(nav) {
  return Array.from(nav.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
}

function getOpenMobileNav() {
  if (!document.body.classList.contains(MOBILE_NAV_ACTIVE_CLASS)) {
    return null;
  }

  return document.querySelector(`${NAV_SELECTOR}.is-visible, ${NAV_SELECTOR}`);
}

function handleMobileNavFocusTrap(event) {
  if (event.key !== 'Tab') {
    return;
  }

  const nav = getOpenMobileNav();
  if (!nav) {
    return;
  }

  const focusableElements = getMobileNavFocusables(nav);
  if (!focusableElements.length) {
    return;
  }

  const { activeElement } = document;
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  if (!nav.contains(activeElement)) {
    event.preventDefault();
    if (event.shiftKey) {
      lastFocusableElement.focus();
    } else {
      firstFocusableElement.focus();
    }
  } else if (event.shiftKey && activeElement === firstFocusableElement) {
    event.preventDefault();
    lastFocusableElement.focus();
  } else if (!event.shiftKey && activeElement === lastFocusableElement) {
    event.preventDefault();
    firstFocusableElement.focus();
  }
}

function bindMobileNavFocusTrap() {
  if (mobileNavFocusTrapBound) {
    return;
  }

  document.addEventListener('keydown', handleMobileNavFocusTrap);
  mobileNavFocusTrapBound = true;
}

Drupal.behaviors.uswds = {
  attach(context) {
    const initialElem = context === document ? document.body : context;
    gessoUswdsAccordions(initialElem);
    banner.on(initialElem);
    inPageNavigation.on(initialElem);
    handbookOnPageNav(initialElem);
    tooltip.on(initialElem);
    table.on(document.body);
    gessoUswdsNavigation(context); // If used with the USWDS accordion component, the navigation must run after it.
    bindMobileNavFocusTrap();
    window.uswdsPresent = true;
  },
};
